import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, ShieldAlert, Heart, Link2, Gavel } from "lucide-react";

const CANON = "https://www.akhusbilfritid.se/forum/regler";

export const Route = createFileRoute("/forum/regler")({
  head: () => ({
    meta: [
      { title: "Forumregler – Fritidsforum för husbil & husvagn" },
      {
        name: "description",
        content:
          "Reglerna i Fritidsforum: så håller vi tonen hjälpsam, vad som gäller för länkar och annonser, hur moderering fungerar och hur du rapporterar innehåll.",
      },
      { property: "og:title", content: "Forumregler – Fritidsforum" },
      {
        property: "og:description",
        content: "Enkla regler som gör Fritidsforum till Sveriges mest hjälpsamma community för husbil och husvagn.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: CANON },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANON }],
  }),
  component: RulesPage,
});

const RULES = [
  {
    icon: Heart,
    title: "1. Var hjälpsam först",
    body:
      "Vi är här för att hjälpa varandra med husbilar och husvagnar. Svara som du själv hade velat få svar: konkret, vänligt och utan nedlåtande ton. Nybörjarfrågor är alltid välkomna.",
  },
  {
    icon: CheckCircle2,
    title: "2. Skriv så andra kan söka och hitta",
    body:
      "Använd en tydlig rubrik (modell, årsmodell, problem) och berätta vad du redan testat. Välj rätt kategori och trådtyp — fråga, diskussion eller bild & resa — och sätt gärna taggar.",
  },
  {
    icon: Link2,
    title: "3. Länkar och annonsering",
    body:
      "Dela gärna länkar som hjälper tråden. Nya konton kan inte lägga externa länkar förrän de har några inlägg bakom sig — ett enkelt spamskydd. Ren försäljning och reklam hör inte hemma i trådarna.",
  },
  {
    icon: ShieldAlert,
    title: "4. Ingen personinformation, hat eller trakasserier",
    body:
      "Publicera inte andras kontaktuppgifter, reg.nummer i konfliktsyfte eller privata konversationer. Personangrepp, hat och trakasserier leder till borttaget inlägg och i förlängningen avstängning.",
  },
  {
    icon: Gavel,
    title: "5. Säkerhet framför tips",
    body:
      "Gasol, el och bromsar är säkerhetskritiskt. Dela erfarenheter, men uppmana alltid till behörig kontroll där det krävs. Råd i forumet ersätter inte besiktning eller certifierad gasolkontroll.",
  },
  {
    icon: CheckCircle2,
    title: "6. Märkta konton och transparens",
    body:
      "Konton märkta Team AK är personal hos AK Husbil & Fritid. Konton märkta Community är redaktionella startkonton som drar igång ämnen. Vi märker dem så att du alltid vet vem du läser.",
  },
];

function RulesPage() {
  return (
    <section className="bg-warm">
      <div className="container-edge py-12 md:py-20">
        <Link to="/forum" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={15} /> Fritidsforum
        </Link>

        <header className="mt-6 max-w-3xl">
          <p className="eyebrow">Fritidsforum</p>
          <h1 className="mt-2 font-display text-4xl leading-tight sm:text-5xl md:text-6xl">Forumregler</h1>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
            Sex enkla regler håller Fritidsforum hjälpsamt, tryggt och läsvärt — även om fem år när någon googlar
            exakt din fråga.
          </p>
        </header>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {RULES.map((r) => (
            <article key={r.title} className="rounded-3xl border border-border bg-card p-6">
              <r.icon size={20} className="text-accent" />
              <h2 className="mt-3 font-display text-xl leading-snug">{r.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.body}</p>
            </article>
          ))}
        </div>

        <div className="mt-10 rounded-3xl border border-border bg-card p-6 md:p-8">
          <h2 className="font-display text-2xl">Moderering och rapportering</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Ser du något som bryter mot reglerna? Tryck <strong>Rapportera</strong> under inlägget eller svaret och
            skriv kort varför. Vi går igenom rapporter löpande. Vi tar bort innehåll som bryter mot reglerna, låser
            trådar som spårat ur och stänger av konton vid upprepade övertramp. Du kan alltid redigera eller ta bort
            dina egna inlägg och svar själv.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/forum"
              className="inline-flex rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Till forumet
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex rounded-full border border-border bg-background px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Kontakta oss
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
